import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, eur2, dateIt } from "@/lib/format";
import { exportCsv } from "@/lib/csv";
import { QUOTE_STATUSES } from "@/lib/constants";
import { PageHeader, QuoteStatusBadge, EmptyState, Field } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Download, Search, Pencil, Trash2, FileDown, Eye, X } from "lucide-react";

const EMPTY = {
  number: "", date: new Date().toISOString().slice(0, 10), recipient: "", description: "",
  line_items: [{ description: "", quantity: 1, unit_price: 0 }], tax_rate: 22, discount: 0,
  validity_date: "", notes: "", status: "Draft", contact_id: "", property_id: "",
};

export default function Quotes() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => { setLoading(true); api.get("/quotes").then((r) => setRows(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => {
    load();
    api.get("/contacts").then((r) => setContacts(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    if (isAdmin) api.get("/auth/users").then((r) => setUsers(r.data));
  }, [isAdmin, load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (search && !`${r.number} ${r.recipient} ${r.description}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rows, search, status]);

  const totals = (f) => {
    const sub = f.line_items.reduce((a, li) => a + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
    const taxable = sub - (Number(f.discount) || 0);
    const tax = taxable * (Number(f.tax_rate) || 0) / 100;
    return { sub, tax, total: taxable + tax };
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({ ...EMPTY, ...q, line_items: q.line_items?.length ? q.line_items : EMPTY.line_items });
    setOpen(true);
  };

  const setLi = (i, key, val) => { const l = [...form.line_items]; l[i] = { ...l[i], [key]: val }; setForm({ ...form, line_items: l }); };
  const addLi = () => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, unit_price: 0 }] });
  const rmLi = (i) => setForm({ ...form, line_items: form.line_items.filter((_, x) => x !== i) });

  const save = async () => {
    if (!form.recipient.trim()) { toast.error("Il destinatario è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, tax_rate: Number(form.tax_rate) || 0, discount: Number(form.discount) || 0,
        line_items: form.line_items.map((li) => ({ description: li.description, quantity: Number(li.quantity) || 0, unit_price: Number(li.unit_price) || 0 })),
      };
      if (editing) await api.put(`/quotes/${editing.id}`, payload);
      else await api.post("/quotes", payload);
      toast.success(editing ? "Preventivo aggiornato" : "Preventivo creato");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async (q) => { try { await api.delete(`/quotes/${q.id}`); toast.success("Preventivo eliminato"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${q.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel download del PDF"); }
  };

  const csv = () => exportCsv("preventivi_investart.csv", filtered, [
    { label: "Numero", key: "number" }, { label: "Data", value: (r) => dateIt(r.date) }, { label: "Destinatario", key: "recipient" },
    { label: "Imponibile", key: "subtotal" }, { label: "Sconto", key: "discount" }, { label: "IVA", key: "tax_amount" },
    { label: "Totale", key: "total" }, { label: "Stato", key: "status" }, { label: "Validità", value: (r) => dateIt(r.validity_date) }, { label: "Responsabile", key: "responsible_name" },
  ]);

  const t = totals(form);

  return (
    <div data-testid="quotes-page">
      <PageHeader title="Preventivi" subtitle={`${filtered.length} preventivi`}>
        <Button variant="outline" onClick={csv} data-testid="export-csv-quotes"><Download className="h-4 w-4 mr-2" />CSV</Button>
        <Button onClick={openNew} data-testid="new-quote-btn"><Plus className="h-4 w-4 mr-2" />Nuovo Preventivo</Button>
      </PageHeader>

      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca per numero, destinatario..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="quotes-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-52" data-testid="quote-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{Object.keys(QUOTE_STATUSES).map((k) => <SelectItem key={k} value={k}>{QUOTE_STATUSES[k].label}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Numero</TableHead><TableHead>Destinatario</TableHead><TableHead>Data</TableHead>
              <TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead>Validità</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{q.number}</TableCell>
                  <TableCell className="text-sm">{q.recipient}</TableCell>
                  <TableCell className="text-sm">{dateIt(q.date)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{eur(q.total)}</TableCell>
                  <TableCell><QuoteStatusBadge status={q.status} /></TableCell>
                  <TableCell className="text-sm">{dateIt(q.validity_date)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setPreview(q)} data-testid={`preview-quote-${q.id}`} title="Anteprima"><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(q)} data-testid={`pdf-quote-${q.id}`} title="PDF"><FileDown className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(q)} data-testid={`edit-quote-${q.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => del(q)} data-testid={`delete-quote-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!loading && filtered.length === 0 && <EmptyState>Nessun preventivo trovato.</EmptyState>}
      </Card>

      {/* Editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica Preventivo" : "Nuovo Preventivo"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <Field label="Numero (auto)"><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="Automatico" /></Field>
            <Field label="Data"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Destinatario *"><Input value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} data-testid="quote-recipient" /></Field>
            <Field label="Stato">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(QUOTE_STATUSES).map((k) => <SelectItem key={k} value={k}>{QUOTE_STATUSES[k].label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Contatto collegato">
              <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessuno</SelectItem>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Immobile collegato">
              <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessuno</SelectItem>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Descrizione" className="sm:col-span-2"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Voci</label>
                <Button size="sm" variant="outline" onClick={addLi} data-testid="add-line-item"><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi voce</Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((li, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input className="flex-1" placeholder="Descrizione" value={li.description} onChange={(e) => setLi(i, "description", e.target.value)} data-testid={`li-desc-${i}`} />
                    <Input className="w-20" type="number" placeholder="Q.tà" value={li.quantity} onChange={(e) => setLi(i, "quantity", e.target.value)} data-testid={`li-qty-${i}`} />
                    <Input className="w-28" type="number" placeholder="Prezzo" value={li.unit_price} onChange={(e) => setLi(i, "unit_price", e.target.value)} data-testid={`li-price-${i}`} />
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-500 shrink-0" onClick={() => rmLi(i)}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <Field label="Aliquota IVA (%)"><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></Field>
            <Field label="Sconto (€)"><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></Field>
            <Field label="Data di validità"><Input type="date" value={form.validity_date} onChange={(e) => setForm({ ...form, validity_date: e.target.value })} /></Field>
            <Field label="Note"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>

            <div className="sm:col-span-2 rounded-lg bg-muted/50 border border-border p-4 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span className="font-mono">{eur2(t.sub)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sconto</span><span className="font-mono">- {eur2(Number(form.discount) || 0)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">IVA ({form.tax_rate}%)</span><span className="font-mono">{eur2(t.tax)}</span></div>
              <div className="flex justify-between font-semibold text-primary pt-1 border-t border-border"><span>Totale</span><span className="font-mono" data-testid="quote-total-preview">{eur2(t.total)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-quote-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Anteprima Preventivo</DialogTitle></DialogHeader>
          {preview && (
            <div className="border border-border rounded-lg p-6 bg-white">
              <div className="flex justify-between items-start border-b-2 border-primary pb-3 mb-4">
                <div className="flex items-center gap-3">
                  <img src="/jemmart-logo.png" alt="JEMMART" className="h-10 w-auto" data-testid="quote-preview-logo" />
                  <div><div className="text-xs font-semibold text-slate-700">JEMMART SRL</div><div className="text-xs text-muted-foreground">Reggio Emilia (RE) · documenti@investart.it</div></div>
                </div>
                <div className="text-right text-sm"><div className="font-bold">{preview.number}</div><div className="text-muted-foreground">{dateIt(preview.date)}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                <div><span className="text-muted-foreground">Destinatario: </span><b>{preview.recipient}</b></div>
                <div><span className="text-muted-foreground">Validità: </span>{dateIt(preview.validity_date)}</div>
              </div>
              {preview.description && <p className="text-sm text-slate-600 mb-4">{preview.description}</p>}
              <table className="w-full text-sm mb-4">
                <thead><tr className="bg-primary text-slate-900"><th className="text-left p-2">Descrizione</th><th className="text-right p-2">Q.tà</th><th className="text-right p-2">Prezzo</th><th className="text-right p-2">Totale</th></tr></thead>
                <tbody>
                  {(preview.line_items || []).map((li, i) => (
                    <tr key={i} className="border-b border-border"><td className="p-2">{li.description}</td><td className="text-right p-2">{li.quantity}</td><td className="text-right p-2 font-mono">{eur2(li.unit_price)}</td><td className="text-right p-2 font-mono">{eur2((li.quantity || 0) * (li.unit_price || 0))}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="ml-auto w-56 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span className="font-mono">{eur2(preview.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Sconto</span><span className="font-mono">- {eur2(preview.discount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">IVA ({preview.tax_rate}%)</span><span className="font-mono">{eur2(preview.tax_amount)}</span></div>
                <div className="flex justify-between font-bold text-slate-900 border-t border-primary pt-1"><span>Totale</span><span className="font-mono">{eur2(preview.total)}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Chiudi</Button>
            <Button onClick={() => downloadPdf(preview)}><FileDown className="h-4 w-4 mr-2" />Scarica PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
